import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { stars } from "@/mocks/stars";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import Tag from "@/components/base/Tag";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButtons from "@/components/feature/ShareButtons";
import BrowseHistoryRail from "@/components/feature/BrowseHistoryRail";
import { useSEO } from "@/hooks/useSEO";
import { addHistory } from "@/hooks/useBrowseHistory";

export default function StarDetail() {
  const { id } = useParams<{ id: string }>();
  const star = stars.find((s) => s.id === id);

  const starWorks = useMemo(() => (star ? works.filter((w) => w.cast.includes(star.name)) : []), [star]);

  const relatedNews = useMemo(() => {
    if (!star) return [];
    const titles = starWorks.map((w) => w.title);
    const matched = news.filter(
      (n) => n.tags.some((t) => titles.includes(t) || t === star.name) || titles.some((t) => n.title.includes(t)),
    );
    return (matched.length > 0 ? matched : news).slice(0, 3);
  }, [star, starWorks]);

  useSEO({
    title: star ? `${star.name} - 朝桐光明星资讯` : "明星详情 - 朝桐光",
    description: star
      ? `${star.name}，朝桐光${star.active}，${star.era}演员。${star.intro.slice(0, 60)}`
      : "朝桐光明星详情页。",
    keywords: star ? `${star.name},朝桐光,明星,${star.tags.join(",")}` : "朝桐光,明星资讯",
  });

  useEffect(() => {
    if (!star) return;
    addHistory({
      type: "star",
      id: star.id,
      title: star.name,
      subtitle: `演员 · ${star.active}`,
      cover: star.avatar,
      path: `/stars/${star.id}`,
    });
  }, [star]);

  if (!star) {
    return (
      <div className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-32 text-center md:px-6">
        <h1 className="text-2xl font-bold text-foreground-950">未找到该明星</h1>
        <p className="mt-3 text-sm text-foreground-600">该明星资料可能已下线或链接有误。</p>
        <Link
          to="/stars"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回明星资讯
        </Link>
      </div>
    );
  }

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "radial-gradient(circle at 25% 25%, oklch(var(--accent-500)) 0, transparent 45%), radial-gradient(circle at 78% 70%, oklch(var(--primary-400)) 0, transparent 50%)" }} />
        <div className="relative mx-auto w-full max-w-page px-4 pb-12 pt-28 md:px-6 md:pb-16 md:pt-36">
          <div className="mb-6 [&_a]:text-background-300 [&_span]:text-background-100 [&_i]:text-background-400">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "明星资讯", to: "/stars" }, { label: star.name }]} />
          </div>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="h-32 w-32 shrink-0 overflow-hidden rounded-lg md:h-40 md:w-40">
              <img
                src={star.avatar}
                alt={`${star.name} 形象照`}
                title={`${star.name} - 朝桐光明星资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Tag tone="accent">{star.active}</Tag>
                <span className="rounded-full border border-background-50/25 px-3 py-0.5 text-xs text-background-200">
                  {star.gender}
                </span>
                <span className="rounded-full border border-background-50/25 px-3 py-0.5 text-xs text-background-200">
                  {star.era}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-3xl font-black text-background-50 md:text-5xl">{star.name}</h1>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-background-300">
                <span>出生：{star.birth}</span>
                <span>代表领域：{star.tags.join(" / ")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-page grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="flex h-5 w-5 items-center justify-center text-accent-600">
              <i className="ri-user-line"></i>
            </span>
            个人简介
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">{star.intro}</p>

          <div className="mt-5">
            <ShareButtons title={`${star.name} - 朝桐光明星资讯`} />
          </div>

          <div className="mt-10">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                <i className="ri-movie-line"></i>
              </span>
              出演作品
            </h2>
            {starWorks.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
                {starWorks.map((w) => (
                  <Link
                    key={w.id}
                    to={`/works/${w.id}`}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                  >
                    <div className="w-full overflow-hidden" style={{ aspectRatio: "2 / 3" }}>
                      <img
                        src={w.cover}
                        alt={`${w.title} 海报`}
                        title={`${w.title} - 朝桐光影视作品`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3">
                      <p className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700">
                        {w.title}
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-500">
                        {w.year} · {w.type}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-foreground-500">暂无收录该明星的影视作品。</p>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-newspaper-line"></i>
              </span>
              最新相关新闻
            </h3>
            <ul className="mt-4 space-y-3">
              {relatedNews.map((n) => (
                <li key={n.id}>
                  <Link
                    to={`/news/${n.id}`}
                    className="group block rounded-md p-2 transition-colors hover:bg-background-100"
                  >
                    <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                      {n.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">{n.date}</p>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/news"
              className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              全部动态
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <BrowseHistoryRail />
        </aside>
      </div>
    </div>
  );
}